import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";

import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsentProvider, GA4Loader } from "@/contexts/CookieConsentContext";
import CookieBanner from "@/components/public/CookieBanner";

import Layout from "@/components/public/Layout";
import Home from "@/pages/public/Home";
import StaticPage from "@/pages/public/StaticPage";
import Contacto from "@/pages/public/Contacto";
import Servicios from "@/pages/public/Servicios";
import EventList from "@/pages/public/EventList";
import ProximasActividades from "@/pages/public/ProximasActividades";
import EventoDetail from "@/pages/public/EventoDetail";
import BuscadorSocios from "@/pages/public/BuscadorSocios";
import NoticiasList from "@/pages/public/NoticiasList";
import NoticiaDetail from "@/pages/public/NoticiaDetail";
import VentajasSocio from "@/pages/public/VentajasSocio";

import ProtectedRoute from "@/components/cms/ProtectedRoute";
import CmsLayout from "@/components/cms/CmsLayout";
import CmsLogin from "@/pages/cms/CmsLogin";
import Dashboard from "@/pages/cms/Dashboard";
import HeroSlidesAdmin from "@/pages/cms/HeroSlidesAdmin";
import HomepageLogosAdmin from "@/pages/cms/HomepageLogosAdmin";
import ResourceAdmin from "@/pages/cms/ResourceAdmin";
import PagesAdmin from "@/pages/cms/PagesAdmin";
import UsersAdmin from "@/pages/cms/UsersAdmin";
import ContactSettingsAdmin from "@/pages/cms/settings/ContactSettingsAdmin";
import NewsletterSettingsAdmin from "@/pages/cms/settings/NewsletterSettingsAdmin";
import AnalyticsSettingsAdmin from "@/pages/cms/settings/AnalyticsSettingsAdmin";
import HomeSettingsAdmin from "@/pages/cms/settings/HomeSettingsAdmin";
import AiTranslationSettingsAdmin from "@/pages/cms/settings/AiTranslationSettingsAdmin";
import BenefitsAdmin from "@/pages/cms/settings/BenefitsAdmin";
import ScriptsSettingsAdmin from "@/pages/cms/settings/ScriptsSettingsAdmin";
import SeoSettingsAdmin from "@/pages/cms/settings/SeoSettingsAdmin";
import TurnstileSettingsAdmin from "@/pages/cms/settings/TurnstileSettingsAdmin";
import TranslationReviewAdmin from "@/pages/cms/TranslationReviewAdmin";
import SubmissionsAdmin from "@/pages/cms/SubmissionsAdmin";
import TeamAdmin from "@/pages/cms/TeamAdmin";
import NewsletterAdmin from "@/pages/cms/NewsletterAdmin";
import AuditLogsAdmin from "@/pages/cms/AuditLogsAdmin";
import MenusAdmin from "@/pages/cms/MenusAdmin";
import HomeServicesAdmin from "@/pages/cms/HomeServicesAdmin";
import LaCamara from "@/pages/public/LaCamara";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PublicShell({ children }) {
  return <Layout>{children}</Layout>;
}

// Category taxonomy for CMS `/admin/actividades` — must stay in sync with
// EventList.jsx, ResourceAdmin.jsx, LanguageContext.jsx and the backend
// Literal enum in server.py. Hoisted so the array reference is stable and
// doesn't force <ResourceAdmin/> to re-render on every parent render.
const ACTIVITY_ADMIN_CATEGORIES = [
  "actividad",
  "encuentros", "networking", "seminarios", "salones_ferias",
  "visitas_reuniones", "actos_institucionales", "webinars",
];

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <CookieConsentProvider>
            <GA4Loader />
            <BrowserRouter>
              <ScrollToTop />
              <CookieBanner />
            <Routes>
              {/* CMS */}
              <Route path="/admin/login" element={<CmsLogin />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<CmsLayout />}>
                  <Route path="/admin" element={<Dashboard />} />
                  <Route path="/admin/hero-slides" element={<HeroSlidesAdmin />} />
                  <Route path="/admin/homepage-logos" element={<HomepageLogosAdmin />} />
                  <Route path="/admin/team" element={<TeamAdmin />} />
                  <Route path="/admin/pages" element={<PagesAdmin />} />
                  <Route path="/admin/news" element={<ResourceAdmin resource="news" />} />
                  <Route path="/admin/actividades" element={<ResourceAdmin resource="actividades" apiResource="events" title="Actividades" categoryOptions={ACTIVITY_ADMIN_CATEGORIES} categoryFilter={ACTIVITY_ADMIN_CATEGORIES} defaultCategory="encuentros" />} />
                  <Route path="/admin/members" element={<ResourceAdmin resource="members" />} />
                  <Route path="/admin/users" element={<UsersAdmin />} />
                  <Route path="/admin/settings" element={<Navigate to="/admin/settings/contact" replace />} />
                  <Route path="/admin/settings/contact" element={<ContactSettingsAdmin />} />
                  <Route path="/admin/settings/newsletter" element={<NewsletterSettingsAdmin />} />
                  <Route path="/admin/settings/analytics" element={<AnalyticsSettingsAdmin />} />
                  <Route path="/admin/settings/home" element={<HomeSettingsAdmin />} />
                  <Route path="/admin/settings/ai" element={<AiTranslationSettingsAdmin />} />
                  <Route path="/admin/settings/benefits" element={<BenefitsAdmin />} />
                  <Route path="/admin/settings/scripts" element={<ScriptsSettingsAdmin />} />
                  <Route path="/admin/settings/seo" element={<SeoSettingsAdmin />} />
                  <Route path="/admin/settings/turnstile" element={<TurnstileSettingsAdmin />} />
                  <Route path="/admin/review" element={<TranslationReviewAdmin />} />
                  <Route path="/admin/submissions" element={<SubmissionsAdmin />} />
                  <Route path="/admin/newsletter" element={<NewsletterAdmin />} />
                  <Route path="/admin/logs" element={<AuditLogsAdmin />} />
                  <Route path="/admin/menus" element={<MenusAdmin />} />
                  <Route path="/admin/home-services" element={<HomeServicesAdmin />} />
                </Route>
              </Route>

              {/* Public */}
              <Route path="/" element={<PublicShell><Home /></PublicShell>} />
              <Route path="/la-camara" element={<PublicShell><LaCamara /></PublicShell>} />
              <Route path="/mision-objetivos" element={<PublicShell><StaticPage slug="mision-objetivos" /></PublicShell>} />
              <Route path="/contacto" element={<PublicShell><Contacto /></PublicShell>} />
              <Route path="/servicios" element={<PublicShell><Servicios /></PublicShell>} />
              <Route path="/servicios/:slug" element={<PublicShell><StaticPage /></PublicShell>} />

              <Route path="/anuario" element={<PublicShell><StaticPage slug="anuario" /></PublicShell>} />
              <Route path="/newsletters" element={<PublicShell><StaticPage slug="newsletters" /></PublicShell>} />
              <Route path="/noticias-socios" element={<PublicShell><NoticiasList /></PublicShell>} />
              <Route path="/noticias-socios/:slug" element={<PublicShell><NoticiaDetail /></PublicShell>} />
              <Route path="/invertir-marruecos" element={<PublicShell><StaticPage slug="invertir-marruecos" /></PublicShell>} />

              <Route path="/actividades" element={<PublicShell><EventList showFilters /></PublicShell>} />
              <Route path="/actividades/proximas" element={<PublicShell><ProximasActividades /></PublicShell>} />
              <Route path="/actividades/:slug" element={<PublicShell><EventoDetail /></PublicShell>} />
              <Route path="/proximas-actividades" element={<PublicShell><ProximasActividades /></PublicShell>} />


              <Route path="/ventajas-socio" element={<PublicShell><VentajasSocio /></PublicShell>} />
              <Route path="/socios-standard" element={<PublicShell><BuscadorSocios tier="standard" /></PublicShell>} />
              <Route path="/socios-premium" element={<PublicShell><BuscadorSocios tier="premium" /></PublicShell>} />
              <Route path="/buscador-socios" element={<PublicShell><BuscadorSocios /></PublicShell>} />

              <Route path="/politica-privacidad" element={<PublicShell><StaticPage slug="politica-privacidad" /></PublicShell>} />
              <Route path="/politica-cookies" element={<PublicShell><StaticPage slug="politica-cookies" /></PublicShell>} />
              <Route path="/aviso-legal" element={<PublicShell><StaticPage slug="aviso-legal" /></PublicShell>} />

              <Route path="*" element={<PublicShell><div className="min-h-[50vh] flex items-center justify-center text-gray-500">Page not found</div></PublicShell>} />
            </Routes>
          </BrowserRouter>
          </CookieConsentProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
